import React, { useState } from "react";
import {
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  Home as HomeIcon,
  Add as AddIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import Scss from "./Sidebar.module.scss";
import Router from "../router/routes";
import { useRecoilValue } from "recoil";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiLogout } from "../hooks/api/useApiLogout";
import {
  canManageUsers,
  hasAnyRole,
  isNotGeneralUser,
  isOwner,
} from "../constants/roles";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [openStatus, setOpenStatus] = useState(false);
  const [openManage, setOpenManage] = useState(false);

  const toggleDrawer = () => {
    if (isOpen) {
      setOpenStatus(false);
      setOpenManage(false);
    }
    setIsOpen(!isOpen);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isOpen ? 240 : 60,
        transition: "width 0.3s",
        "& .MuiDrawer-paper": {
          width: isOpen ? 240 : 60,
          boxSizing: "border-box",
          overflowX: "hidden",
          transition: "width 0.3s",
          backgroundColor: "#f5f9fd",
        },
      }}
    >
      {/* トグルボタン */}
      <div
        className={`${Scss.toggleWrapper} ${
          isOpen ? Scss.toggleOpen : Scss.toggleClosed
        }`}
      >
        <IconButton onClick={toggleDrawer}>
          {isOpen ? <ChevronLeft /> : <MenuIcon />}
        </IconButton>
      </div>

      <RouterSidebar
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        openStatus={openStatus}
        setOpenStatus={setOpenStatus}
        openManage={openManage}
        setOpenManage={setOpenManage}
      />
    </Drawer>
  );
};

const RouterSidebar: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openStatus: boolean;
  setOpenStatus: React.Dispatch<React.SetStateAction<boolean>>;
  openManage: boolean;
  setOpenManage: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  isOpen,
  setIsOpen,
  openStatus,
  setOpenStatus,
  openManage,
  setOpenManage,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginUser = useRecoilValue(loginUserSelector);

  const handleClick = (
    setter?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (setter) setter((prev) => !prev);
  };

  const iconColor = "#f44336";
  const menuItems = [
    {
      text: "HOME",
      icon: <HomeIcon sx={{ color: iconColor }} />,
      path: Router.toppage,
      expandIcon: true,
    },
    {
      text: "商品登録",
      icon: <AddIcon sx={{ color: iconColor }} />,
      path: Router.productRergistration,
      expandIcon: true,
    },
    {
      text: "ステータス",
      icon: <InventoryIcon sx={{ color: iconColor }} />,
      children: [
        { text: "入荷待ち", path: Router.pendingReceipt },
        { text: "梱包待ち", path: Router.pendingPackaging },
        { text: "プラン作成待ち", path: Router.pendingPlan },
        { text: "出荷待ち", path: Router.pendingShipment },
        { text: "出荷済み", path: Router.shipped },
        { text: "要確認", path: Router.needsReview },
        { text: "一覧", path: Router.allList },
        ...(isNotGeneralUser(loginUser?.permissionId)
          ? [{ text: "ごみ箱", path: Router.trustBox }]
          : []),
      ],
      open: openStatus,
      setOpen: setOpenStatus,
    },
    {
      text: "管理",
      icon: <PersonIcon sx={{ color: iconColor }} />,
      children: [
        ...(canManageUsers(loginUser?.permissionId)
          ? [{ text: "ユーザー管理", path: Router.managementUsers }]
          : []),

        ...(isOwner(loginUser?.permissionId)
          ? [
              { text: "拠点一覧", path: Router.baseList },
              { text: "管理者", path: Router.management },
              // { text: "設定", path: "/" },
              { text: "レポート", path: Router.report },
            ]
          : []),

        ...(hasAnyRole(loginUser?.permissionId)
          ? [{ text: "ユーザー情報", path: Router.userInfo }]
          : []),

        ...(canManageUsers(loginUser?.permissionId)
          ? [{ text: "設定", path: Router.setting }]
          : []),
      ],
      open: openManage,
      setOpen: setOpenManage,
    },
    ...(loginUser?.isOperationLogViewer
      ? [
          {
            text: "操作ログ",
            icon: <HistoryIcon sx={{ color: iconColor }} />,
            path: Router.operationLogs,
            expandIcon: true,
          },
        ]
      : []),
    {
      text: "ログアウト",
      icon: <LogoutIcon sx={{ color: iconColor }} />,
      path: Router.logout,
      expandIcon: true,
    },
  ];

  return (
    <List>
      {menuItems.map((item, index) => (
        <div key={item.text}>
          <ListItem className={Scss.list_item} disablePadding>
            <ListItemButton
              className={`${Scss.listItemButton} ${
                location.pathname === item.path ? Scss.selected : ""
              }`}
              onClick={() => {
                if (item.children) {
                  if (!isOpen) setIsOpen(true);
                  handleClick(item.setOpen);
                } else if (item.path) {
                  navigate(item.path);
                }
              }}
              selected={location.pathname === item.path}
            >
              <ListItemIcon
                className={`${Scss.listItemIcon} ${
                  isOpen ? Scss.iconMargin : ""
                }`}
              >
                {item.icon}
              </ListItemIcon>
              {isOpen && <ListItemText primary={item.text} />}
              {isOpen &&
                item.children &&
                (item.open ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          </ListItem>

          {item.children && (
            <Collapse in={item.open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children.map((child) => (
                  <ListItemButton
                    key={child.text}
                    onClick={() => navigate(child.path)}
                    selected={location.pathname === child.path}
                  >
                    <ListItemText
                      primary={child.text}
                      primaryTypographyProps={{ fontWeight: "bold" }}
                      className={Scss.subItemText}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          )}

          {index !== menuItems.length - 1 && <Divider />}
        </div>
      ))}
    </List>
  );
};

export default Sidebar;
